import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '../components/Icon';
import Toggle from '../components/Toggle';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import {
  BillingCycle,
  SubscriptionPlan,
  SubscriptionPlanInput,
  PlanSubscriberSummary,
  formatPlanPrice,
  getPlanSubscriberSummary,
  listSubscriptionPlans,
  movePlanSubscribers,
  syncSubscriptionPlanToStripe,
  toggleSubscriptionPlan,
  updateSubscriptionPlan,
} from '../api/subscriptionPlans';
import { extractApiError } from '../api/legalServices';

type ModalMode =
  | { kind: 'closed' }
  | { kind: 'edit'; plan: SubscriptionPlan };

interface FormState {
  name: string;
  description: string;
  price: string;
  billingCycle: BillingCycle;
  features: string[];
  isMostPopular: boolean;
  isActive: boolean;
  displayOrder: string;
  totalListing: string;
  unlimitedListings: boolean;
  trialDays: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  price: '',
  billingCycle: 'monthly',
  features: [''],
  isMostPopular: false,
  isActive: true,
  displayOrder: '0',
  totalListing: '',
  unlimitedListings: true,
  trialDays: '0',
};

const fromPlan = (p: SubscriptionPlan): FormState => ({
  name: p.name ?? '',
  description: p.description ?? '',
  price: p.price === null || p.price === undefined ? '' : String(p.price),
  billingCycle: p.billingCycle ?? 'monthly',
  features: p.features && p.features.length > 0 ? [...p.features] : [''],
  isMostPopular: !!p.isMostPopular,
  isActive: p.isActive,
  displayOrder: String(p.displayOrder ?? 0),
  totalListing:
    p.totalListing === null || p.totalListing === undefined
      ? ''
      : String(p.totalListing),
  unlimitedListings: p.totalListing === null || p.totalListing === undefined,
  trialDays: String(p.trialDays ?? 0),
});

// Stripe's minimum USD charge, and the most it accepts for one price.
const MIN_PRICE = 0.5;
const MAX_PRICE = 999999.99;

const toPayload = (f: FormState): SubscriptionPlanInput => ({
  name: f.name.trim(),
  // A changed price becomes a new Stripe price on save (billing cycle stays
  // locked — only monthly plans are offered).
  price: Number(f.price),
  description: f.description.trim() || undefined,
  features: f.features.map((s) => s.trim()).filter(Boolean),
  isMostPopular: f.isMostPopular,
  isActive: f.isActive,
  displayOrder: Number(f.displayOrder) || 0,
  totalListing: f.unlimitedListings
    ? null
    : f.totalListing.trim() === ''
    ? null
    : Math.max(0, Math.floor(Number(f.totalListing))),
  trialDays: Math.max(0, Math.floor(Number(f.trialDays) || 0)),
});

const priceText = (n: number): string => (n % 1 === 0 ? `$${n}` : `$${n.toFixed(2)}`);

interface StatProps {
  icon: string;
  label: string;
  value: string;
  hint?: string;
  delay?: number;
}

const Stat: React.FC<StatProps> = ({ icon, label, value, hint, delay = 0 }) => (
  <div
    className="card flex-1 min-w-[200px] p-5 animate-fadeIn"
    style={{ animationDelay: delay + 'ms' }}
  >
    <div className="flex items-center gap-3 mb-2">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: 'var(--brand-bg)' }}
      >
        <Icon name={icon} size={20} color="var(--brand)" />
      </div>
      <div className="text-[13px] text-g500 font-medium">{label}</div>
    </div>
    <div className="text-[26px] font-extrabold text-ink leading-tight">
      {value}
    </div>
    {hint && <div className="text-[12px] text-g400 mt-1">{hint}</div>}
  </div>
);

const formatListingLimit = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return 'Unlimited listings';
  if (n === 0) return 'No listings';
  if (n === 1) return '1 listing';
  return 'Up to ' + n + ' listings';
};

interface StripeStatusProps {
  plan: SubscriptionPlan;
  stripeEnabled: boolean;
  stripeMode: 'test' | 'live';
  syncing: boolean;
  onSync: () => void;
}

/** Whether a paid plan has its Stripe product + price, with a sync action. */
const StripeStatus: React.FC<StripeStatusProps> = ({
  plan,
  stripeEnabled,
  stripeMode,
  syncing,
  onSync,
}) => {
  const priceId =
    plan.billingCycle === 'yearly' ? plan.stripeYearlyPriceId : plan.stripeMonthlyPriceId;
  const inStripe = stripeEnabled && !!plan.stripeProductId && !!priceId;
  const color = inStripe ? 'var(--green)' : 'var(--yellow)';

  return (
    <div
      className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg"
      style={{
        background: inStripe ? 'var(--green-bg)' : 'var(--yellow-bg)',
        border: '1px solid var(--g100)',
      }}
    >
      <Icon name="credit-card" size={14} color={color} />
      <span
        className="text-[12.5px] font-semibold flex-1 min-w-0 truncate"
        style={{ color }}
        title={inStripe ? `${plan.stripeProductId} · ${priceId}` : undefined}
      >
        {!stripeEnabled
          ? 'Stripe keys not set'
          : inStripe
          ? `In Stripe (${stripeMode} mode)`
          : 'Not in Stripe yet'}
      </span>
      {stripeEnabled && (
        <button
          type="button"
          className="text-[12px] font-semibold ml-1 disabled:opacity-50"
          style={{ color: inStripe ? 'var(--g600)' : 'var(--yellow)' }}
          disabled={syncing}
          onClick={onSync}
          title="Create or repair this plan's Stripe product and price"
        >
          {syncing ? 'Syncing...' : inStripe ? 'Re-sync' : 'Sync to Stripe'}
        </button>
      )}
    </div>
  );
};

const Subscriptions: React.FC = () => {
  const { toast } = useToast();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Only monthly plans are offered; the yearly tier is retired.
  const cycle: BillingCycle = 'monthly';
  const [modal, setModal] = useState<ModalMode>({ kind: 'closed' });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState<boolean>(false);
  const [busyToggleId, setBusyToggleId] = useState<number | null>(null);
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [stripeEnabled, setStripeEnabled] = useState<boolean>(false);
  const [stripeMode, setStripeMode] = useState<'test' | 'live'>('test');
  // Renewing subscribers per plan id (paid, active plans only).
  const [summaries, setSummaries] = useState<Record<number, PlanSubscriberSummary>>({});
  // Edit form: also move existing subscribers when the price changes.
  const [moveExisting, setMoveExisting] = useState<boolean>(false);
  const [confirmMove, setConfirmMove] = useState<SubscriptionPlan | null>(null);
  const [movingId, setMovingId] = useState<number | null>(null);

  const loadSummary = useCallback(async (planId: number) => {
    try {
      const s = await getPlanSubscriberSummary(planId);
      setSummaries((prev) => ({ ...prev, [planId]: s }));
    } catch {
      /* counts are informational; the page works without them */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listSubscriptionPlans();
      setPlans(res.plans);
      setStripeEnabled(res.stripeEnabled);
      setStripeMode(res.stripeMode);
      res.plans
        .filter((p) => p.isActive && Number(p.price) > 0)
        .forEach((p) => loadSummary(p.id));
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load plans'));
    } finally {
      setLoading(false);
    }
  }, [loadSummary]);

  useEffect(() => {
    load();
  }, [load]);

  const visiblePlans = useMemo(
    () => plans.filter((p) => p.billingCycle === cycle && p.isActive),
    [plans, cycle]
  );

  /*
   * These cards describe the plans agents can actually sign up for, so they
   * count active plans only — matching the list below, which filters the same
   * way. Counting retired plans here made the header disagree with the page
   * ("7 plans" above a list showing 1).
   */
  const activePlans = useMemo(() => plans.filter((p) => p.isActive), [plans]);
  const totalPlans = activePlans.length;
  const retiredPlans = plans.length - totalPlans;
  const monthlyCount = activePlans.filter(
    (p) => p.billingCycle === 'monthly'
  ).length;
  const popularPlan = activePlans.find(
    (p) => p.isMostPopular && p.billingCycle === cycle
  );

  const openEdit = (p: SubscriptionPlan) => {
    setForm(fromPlan(p));
    setMoveExisting(false);
    setModal({ kind: 'edit', plan: p });
    if (Number(p.price) > 0) loadSummary(p.id);
  };

  /** Move a plan's renewing subscribers to its current price; toasts the outcome. */
  const runMove = async (p: SubscriptionPlan) => {
    setMovingId(p.id);
    try {
      const { message, result, summary } = await movePlanSubscribers(p.id);
      setSummaries((prev) => ({ ...prev, [p.id]: summary }));
      toast(message, result.failed.length ? 'error' : 'success');
    } catch (err) {
      toast(extractApiError(err, 'Moving subscribers failed'), 'error');
    } finally {
      setMovingId(null);
    }
  };

  const closeModal = () => {
    if (saving) return;
    setModal({ kind: 'closed' });
  };

  const setField = <K extends keyof FormState>(
    key: K,
    value: FormState[K]
  ) => setForm((f) => ({ ...f, [key]: value }));

  const setFeatureAt = (idx: number, value: string) =>
    setForm((f) => {
      const next = [...f.features];
      next[idx] = value;
      return { ...f, features: next };
    });

  const addFeature = () =>
    setForm((f) => ({ ...f, features: [...f.features, ''] }));

  const removeFeature = (idx: number) =>
    setForm((f) => {
      const next = f.features.filter((_, i) => i !== idx);
      return { ...f, features: next.length ? next : [''] };
    });

  const handleToggle = async (p: SubscriptionPlan) => {
    setBusyToggleId(p.id);
    setPlans((prev) =>
      prev.map((x) => (x.id === p.id ? { ...x, isActive: !x.isActive } : x))
    );
    try {
      const updated = await toggleSubscriptionPlan(p.id);
      setPlans((prev) => prev.map((x) => (x.id === p.id ? updated : x)));
      toast(
        updated.name + ' ' + (updated.isActive ? 'activated' : 'deactivated'),
        updated.isActive ? 'success' : 'info'
      );
    } catch (err) {
      setPlans((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, isActive: p.isActive } : x))
      );
      toast(extractApiError(err, 'Failed to toggle plan'), 'error');
    } finally {
      setBusyToggleId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (modal.kind !== 'edit') return;
    if (!form.name.trim()) {
      toast('Plan name is required', 'error');
      return;
    }
    if (
      !form.unlimitedListings &&
      form.totalListing.trim() !== '' &&
      Number(form.totalListing) < 0
    ) {
      toast('Total listings cannot be negative', 'error');
      return;
    }
    if (priceChanged && !priceValid) {
      toast(`Price must be between $${MIN_PRICE.toFixed(2)} and $${MAX_PRICE.toLocaleString('en-US')}`, 'error');
      return;
    }
    setSaving(true);
    try {
      const { plan: updated, stripe } = await updateSubscriptionPlan(
        modal.plan.id,
        toPayload(form)
      );
      setPlans((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      if (stripe && stripe.error) {
        toast(`${updated.name} saved, but Stripe sync failed: ${stripe.error}`, 'error');
      } else if (priceChanged && stripe?.ok && !stripe.skipped) {
        toast(
          `${updated.name} updated — new Stripe price of ${priceText(Number(updated.price))}/month created`,
          'success'
        );
      } else {
        toast(updated.name + ' updated', 'success');
      }
      setModal({ kind: 'closed' });
      if (priceChanged && stripe?.ok && !stripe.skipped) {
        if (moveExisting && existingSubscribers > 0) await runMove(updated);
        else loadSummary(updated.id);
      }
    } catch (err) {
      toast(extractApiError(err, 'Save failed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async (p: SubscriptionPlan) => {
    setSyncingId(p.id);
    try {
      const { plan: updated } = await syncSubscriptionPlanToStripe(p.id);
      setPlans((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      toast(`${updated.name} is up to date in Stripe`, 'success');
    } catch (err) {
      toast(extractApiError(err, 'Stripe sync failed'), 'error');
    } finally {
      setSyncingId(null);
    }
  };

  const originalPrice = modal.kind === 'edit' ? Number(modal.plan.price) : 0;
  const formPrice = Number(form.price);
  const priceChanged =
    modal.kind === 'edit' && form.price.trim() !== '' && formPrice !== originalPrice;
  const priceValid =
    form.price.trim() !== '' &&
    Number.isFinite(formPrice) &&
    formPrice >= MIN_PRICE &&
    formPrice <= MAX_PRICE;
  const editSummary = modal.kind === 'edit' ? summaries[modal.plan.id] : undefined;
  const existingSubscribers = editSummary?.subscribers ?? 0;

  const modalTitle =
    modal.kind === 'edit' ? 'Edit: ' + modal.plan.name : '';

  return (
    <div>
      {/* Stats row */}
      <div className="flex gap-4 flex-wrap mb-6">
        <Stat
          icon="credit-card"
          label="Active Plans"
          value={String(totalPlans)}
          hint={retiredPlans > 0 ? retiredPlans + ' retired' : 'none retired'}
          delay={0}
        />
        <Stat
          icon="bar-chart-3"
          label="Monthly Plans"
          value={String(monthlyCount)}
          hint="Billed every month"
          delay={80}
        />
        <Stat
          icon="star"
          label="Most Popular"
          value={popularPlan ? popularPlan.name : '—'}
          hint={popularPlan ? cycle + ' tier' : 'Mark a plan as popular'}
          delay={160}
        />
      </div>

      {/* Filter row */}
      <div
        className="card flex justify-between items-center flex-wrap gap-3 p-4 mb-6 animate-fadeIn"
        style={{ animationDelay: '320ms' }}
      >
        <div className="flex items-center gap-4 flex-wrap">
          <div className="text-sm text-g500">
            Viewing <strong className="text-ink">{visiblePlans.length}</strong>{' '}
            {cycle} plan{visiblePlans.length === 1 ? '' : 's'}
          </div>
        </div>
      </div>

      {/* Error / Loading / Empty states */}
      {loadError && (
        <div
          className="card p-5 mb-5"
          role="alert"
          style={{
            background: 'var(--red-bg)',
            color: 'var(--red)',
            border: '1px solid rgba(220,38,38,.12)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="x-circle" size={18} color="var(--red)" />
            <strong>Couldn't load plans</strong>
          </div>
          <div className="text-sm mb-3">{loadError}</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {loading && !loadError && (
        <div className="card p-12 text-center text-g500">
          <Icon name="package" size={28} color="var(--g400)" /> Loading...
        </div>
      )}

      {!loading && !loadError && visiblePlans.length === 0 && (
        <div className="card p-16 flex flex-col items-center text-center">
          <div
            className="rounded-[20px] flex items-center justify-center mb-5"
            style={{ width: 72, height: 72, background: 'var(--g100)' }}
          >
            <Icon name="credit-card" size={32} color="var(--g400)" />
          </div>
          <div className="text-lg font-semibold text-ink mb-1.5">
            No active {cycle} plans
          </div>
          <div className="text-sm text-g500" style={{ maxWidth: 420 }}>
            New plans are managed elsewhere. Active plans will appear here for
            editing.
          </div>
        </div>
      )}

      {/* Plan cards */}
      {!loading && !loadError && visiblePlans.length > 0 && (
        <div
          className="grid gap-6"
          style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            alignItems: 'stretch',
          }}
        >
          {visiblePlans.map((p, ix) => {
            const { amount, suffix } = formatPlanPrice(p.price, p.billingCycle);
            const popular = p.isMostPopular;
            return (
              <div
                key={p.id}
                className="card relative flex flex-col animate-fadeIn"
                style={{
                  animationDelay: ix * 80 + 'ms',
                  borderColor: popular ? 'var(--brand)' : 'var(--g100)',
                  borderWidth: popular ? 2 : 1,
                  boxShadow: popular
                    ? '0 8px 28px rgba(225,1,116,0.16)'
                    : '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                  opacity: p.isActive ? 1 : 0.6,
                  transform: popular ? 'translateY(-4px)' : undefined,
                  transition: 'transform .2s ease',
                  minHeight: 540,
                }}
              >
                {popular && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 -top-3.5 flex items-center gap-1 px-4 py-1.5 rounded-full text-[11px] font-bold text-white whitespace-nowrap"
                    style={{
                      background: 'var(--brand)',
                      boxShadow: '0 4px 12px rgba(225,1,116,0.3)',
                      letterSpacing: '0.4px',
                    }}
                  >
                    <Icon name="star" size={12} color="#fff" />
                    MOST POPULAR
                  </div>
                )}

                <div
                  className="p-7 pb-6"
                  style={{
                    background: popular
                      ? 'linear-gradient(180deg, var(--brand-light) 0%, transparent 100%)'
                      : 'transparent',
                  }}
                >
                  <div className="text-[22px] font-extrabold mb-1">{p.name}</div>
                  {p.description ? (
                    <div className="text-[13px] text-g500 leading-relaxed mb-5 min-h-[40px]">
                      {p.description}
                    </div>
                  ) : (
                    <div className="text-[13px] text-g400 italic mb-5 min-h-[40px]">
                      No description provided
                    </div>
                  )}

                  <div className="flex items-baseline gap-2">
                    <span
                      className="text-[44px] font-extrabold leading-none"
                      style={{ color: popular ? 'var(--brand)' : 'var(--ink)' }}
                    >
                      {amount}
                    </span>
                    {suffix && (
                      <span className="text-sm font-medium text-g500">
                        {suffix}
                      </span>
                    )}
                  </div>

                  <div
                    className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg"
                    style={{
                      background: 'var(--g50)',
                      border: '1px solid var(--g100)',
                    }}
                  >
                    <Icon name="building-2" size={14} color="var(--brand)" />
                    <span
                      className="text-[12.5px] font-semibold"
                      style={{ color: 'var(--g700)' }}
                    >
                      {formatListingLimit(p.totalListing)}
                    </span>
                  </div>

                  {(p.trialDays ?? 0) > 0 && (
                    <div
                      className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg"
                      style={{
                        background: 'var(--brand-bg)',
                        border: '1px solid var(--g100)',
                      }}
                    >
                      <Icon name="calendar" size={14} color="var(--brand)" />
                      <span
                        className="text-[12.5px] font-semibold"
                        style={{ color: 'var(--brand)' }}
                      >
                        {p.trialDays}-day free trial
                      </span>
                    </div>
                  )}

                  {Number(p.price) > 0 && (
                    <StripeStatus
                      plan={p}
                      stripeEnabled={stripeEnabled}
                      stripeMode={stripeMode}
                      syncing={syncingId === p.id}
                      onSync={() => handleSync(p)}
                    />
                  )}

                  {stripeEnabled && (summaries[p.id]?.onOlderPrice ?? 0) > 0 && (
                    <div
                      className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg"
                      style={{ background: 'var(--blue-bg)', border: '1px solid var(--g100)' }}
                    >
                      <Icon name="users" size={14} color="var(--blue)" />
                      <span
                        className="text-[12.5px] font-semibold flex-1 min-w-0"
                        style={{ color: 'var(--blue)' }}
                      >
                        {summaries[p.id].onOlderPrice} subscriber
                        {summaries[p.id].onOlderPrice === 1 ? '' : 's'} on an older price
                      </span>
                      <button
                        type="button"
                        className="text-[12px] font-semibold disabled:opacity-50"
                        style={{ color: 'var(--blue)' }}
                        disabled={movingId === p.id}
                        onClick={() => setConfirmMove(p)}
                      >
                        {movingId === p.id ? 'Moving...' : `Move to ${priceText(Number(p.price))}`}
                      </button>
                    </div>
                  )}
                </div>

                <div className="px-7 flex-1">
                  <div
                    className="text-[11px] font-bold uppercase tracking-wider text-g500 mb-3"
                    style={{ letterSpacing: '0.6px' }}
                  >
                    What's included
                  </div>
                  <ul className="list-none flex flex-col gap-3 p-0 m-0">
                    {p.features.length === 0 ? (
                      <li className="text-[13px] text-g400 italic">
                        No features listed
                      </li>
                    ) : (
                      p.features.map((f, fi) => (
                        <li
                          key={fi}
                          className="flex items-start gap-2.5 text-[14px] text-g700 leading-relaxed"
                        >
                          <span
                            className="rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{
                              width: 18,
                              height: 18,
                              background: popular
                                ? 'var(--brand-bg)'
                                : 'var(--green-bg)',
                            }}
                          >
                            <Icon
                              name="check-circle"
                              size={12}
                              color={popular ? 'var(--brand)' : 'var(--green)'}
                            />
                          </span>
                          {f}
                        </li>
                      ))
                    )}
                  </ul>
                </div>

                <div
                  className="flex items-center justify-between p-5 mt-6"
                  style={{ borderTop: '1px solid var(--g100)' }}
                >
                  <div className="flex items-center gap-2">
                    <Toggle
                      on={p.isActive}
                      onChange={() => {
                        if (busyToggleId !== p.id) handleToggle(p);
                      }}
                      ariaLabel={'Toggle ' + p.name}
                    />
                    <span
                      className="text-xs font-semibold"
                      style={{
                        color: p.isActive ? 'var(--green)' : 'var(--g500)',
                      }}
                    >
                      {p.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <button
                    className={
                      popular
                        ? 'btn btn-primary btn-sm'
                        : 'btn btn-secondary btn-sm'
                    }
                    onClick={() => openEdit(p)}
                  >
                    <Icon
                      name="pencil"
                      size={12}
                      color={popular ? '#fff' : 'currentColor'}
                    />{' '}
                    Edit
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tip banner */}
      {!loading && !loadError && visiblePlans.length > 0 && (
        <div
          className="card p-5 mt-6 flex items-start gap-3 animate-fadeIn"
          style={{
            background: 'var(--blue-bg)',
            border: '1px solid rgba(37,99,235,.12)',
            animationDelay: '400ms',
          }}
        >
          <div
            className="rounded-[10px] flex items-center justify-center flex-shrink-0"
            style={{ width: 36, height: 36, background: '#fff' }}
          >
            <Icon name="bell" size={18} color="var(--blue)" />
          </div>
          <div className="flex-1">
            <div
              className="text-[14px] font-semibold mb-1"
              style={{ color: 'var(--blue)' }}
            >
              Plan management tips
            </div>
            <div className="text-[13px] text-g600 leading-relaxed">
              Saving a plan updates its Stripe product. Changing the price
              creates a new Stripe price for new sign-ups; existing subscribers
              keep the price they signed up at. Use the active toggle to retire
              a plan; new merchant signups will skip inactive plans automatically.
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      <Modal
        open={modal.kind !== 'closed'}
        title={modalTitle}
        onClose={closeModal}
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="form-label">Plan Name</label>
            <input
              className="form-input"
              placeholder="e.g. Pro"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              autoFocus
              disabled={saving}
            />
          </div>

          <div className="mb-4">
            <label className="form-label">Description</label>
            <textarea
              rows={2}
              className="form-input"
              placeholder="Short description..."
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              disabled={saving}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="mb-4">
              <label className="form-label" htmlFor="plan-price">
                Price ($)
              </label>
              <input
                id="plan-price"
                type="number"
                step="0.01"
                min={MIN_PRICE}
                max={MAX_PRICE}
                inputMode="decimal"
                className="form-input"
                value={form.price}
                onChange={(e) => setField('price', e.target.value)}
                disabled={saving}
                style={priceChanged && !priceValid ? { borderColor: 'var(--red)' } : undefined}
              />
            </div>
            <div className="mb-4">
              <label className="form-label">
                Billing Cycle
                <span
                  className="text-g400 font-normal ml-1"
                  style={{ fontSize: 11 }}
                >
                  (locked)
                </span>
              </label>
              <select
                className="form-input"
                value={form.billingCycle}
                disabled
                style={{
                  opacity: 0.6,
                  cursor: 'not-allowed',
                  background: 'var(--g50)',
                }}
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
          </div>

          {priceChanged && (
            <div
              className="flex items-start gap-2 px-3.5 py-2.5 rounded-xl text-[12.5px] leading-relaxed -mt-1 mb-4"
              style={
                priceValid
                  ? { background: 'var(--yellow-bg)', color: 'var(--yellow)' }
                  : { background: 'var(--red-bg)', color: 'var(--red)' }
              }
            >
              <Icon name="info" size={15} color={priceValid ? 'var(--yellow)' : 'var(--red)'} />
              <span>
                {priceValid ? (
                  <>
                    Saving creates a new {priceText(formPrice)}/month price in Stripe for new
                    sign-ups.{' '}
                    {!editSummary
                      ? 'Checking existing subscribers...'
                      : existingSubscribers === 0
                      ? 'No one is subscribed to this plan yet, so nobody else is affected.'
                      : moveExisting
                      ? `Your ${existingSubscribers} existing subscriber${existingSubscribers === 1 ? '' : 's'} move to the new price from their next payment.`
                      : `Your ${existingSubscribers} existing subscriber${existingSubscribers === 1 ? '' : 's'} keep paying their current price.`}
                  </>
                ) : (
                  <>
                    Enter a price between ${MIN_PRICE.toFixed(2)} (Stripe's minimum) and $
                    {MAX_PRICE.toLocaleString('en-US')}.
                  </>
                )}
              </span>
            </div>
          )}

          {priceChanged && priceValid && existingSubscribers > 0 && (
            <fieldset className="mb-4 -mt-1" disabled={saving}>
              <legend className="form-label">
                Existing subscribers ({existingSubscribers})
              </legend>
              {[
                {
                  value: false,
                  title: 'Keep their current price',
                  text: 'Only new sign-ups pay the new price.',
                },
                {
                  value: true,
                  title: `Move them to ${priceText(formPrice)}/month from their next payment`,
                  text: "Nothing is charged now. Each subscriber gets an in-app notification with the date it takes effect.",
                },
              ].map((o) => {
                const selected = moveExisting === o.value;
                return (
                  <label
                    key={String(o.value)}
                    className="flex items-start gap-2.5 rounded-xl px-3.5 py-2.5 mb-2 cursor-pointer transition-colors"
                    style={{
                      border: `1.5px solid ${selected ? 'var(--brand)' : 'var(--g200)'}`,
                      background: selected ? 'var(--brand-light)' : '#fff',
                    }}
                  >
                    <input
                      type="radio"
                      name="move-existing"
                      className="mt-1 accent-[var(--brand)]"
                      checked={selected}
                      onChange={() => setMoveExisting(o.value)}
                    />
                    <span>
                      <span className="block text-[13.5px] font-semibold text-ink">{o.title}</span>
                      <span className="block text-[12.5px] text-g500 leading-snug mt-0.5">{o.text}</span>
                    </span>
                  </label>
                );
              })}
            </fieldset>
          )}

          <div className="mb-4">
            <label className="form-label">Listing Limit</label>
            <div className="flex items-center gap-2.5 mb-2 py-1">
              <Toggle
                on={form.unlimitedListings}
                onChange={() =>
                  setField('unlimitedListings', !form.unlimitedListings)
                }
              />
              <span className="text-[13px] text-g500">
                {form.unlimitedListings
                  ? 'Unlimited listings'
                  : 'Set a specific cap'}
              </span>
            </div>
            <input
              type="number"
              min="0"
              step="1"
              className="form-input"
              placeholder="e.g. 15"
              value={form.unlimitedListings ? '' : form.totalListing}
              onChange={(e) => setField('totalListing', e.target.value)}
              disabled={saving || form.unlimitedListings}
              style={{ opacity: form.unlimitedListings ? 0.5 : 1 }}
            />
            <div className="text-[12px] text-g400 mt-1">
              Maximum number of property listings a merchant on this plan can
              publish. Leave "Unlimited" on for no cap.
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label">Free Trial (days)</label>
            <input
              type="number"
              min="0"
              step="1"
              className="form-input"
              placeholder="e.g. 15"
              value={form.trialDays}
              onChange={(e) => setField('trialDays', e.target.value)}
              disabled={saving}
            />
            <div className="text-[12px] text-g400 mt-1">
              Days of free trial before the first charge. The card is collected
              up front; Stripe bills automatically when the trial ends. 0 = no
              trial.
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label">Features</label>
            {form.features.map((f, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <input
                  className="form-input"
                  placeholder="e.g. Unlimited listings"
                  value={f}
                  onChange={(e) => setFeatureAt(i, e.target.value)}
                  disabled={saving}
                />
                <button
                  type="button"
                  onClick={() => removeFeature(i)}
                  disabled={saving}
                  className="flex items-center justify-center"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    border: '1px solid var(--g200)',
                    background: '#fff',
                    color: 'var(--g400)',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                  aria-label="Remove feature"
                >
                  <Icon name="x-circle" size={14} color="var(--g500)" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addFeature}
              className="btn btn-ghost btn-sm mt-1"
              disabled={saving}
            >
              <Icon name="plus" size={14} /> Add Feature
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="mb-4">
              <label className="form-label">Display Order</label>
              <input
                type="number"
                min="0"
                className="form-input"
                value={form.displayOrder}
                onChange={(e) => setField('displayOrder', e.target.value)}
                disabled={saving}
              />
            </div>
            <div className="mb-4">
              <label className="form-label">Status</label>
              <div className="flex items-center gap-2.5 py-1.5">
                <Toggle
                  on={form.isActive}
                  onChange={() => setField('isActive', !form.isActive)}
                />
                <span className="text-[13px] text-g500">
                  {form.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-2">
            <label className="form-label">Most Popular Badge</label>
            <div className="flex items-center gap-2.5 py-1.5">
              <Toggle
                on={form.isMostPopular}
                onChange={() => setField('isMostPopular', !form.isMostPopular)}
              />
              <span className="text-[13px] text-g500">
                {form.isMostPopular
                  ? 'Highlighted as most popular'
                  : 'Normal plan'}
              </span>
            </div>
          </div>

          <div
            className="flex gap-2.5 justify-end mt-6"
            style={{ flexWrap: 'wrap' }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeModal}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmMove !== null}
        title="Move subscribers to the current price?"
        variant="primary"
        confirmLabel="Move subscribers"
        busy={confirmMove !== null && movingId === confirmMove.id}
        message={
          confirmMove ? (
            <>
              {summaries[confirmMove.id]?.onOlderPrice ?? 0} subscriber
              {(summaries[confirmMove.id]?.onOlderPrice ?? 0) === 1 ? '' : 's'} on an older price
              will pay{' '}
              <strong style={{ color: 'var(--ink)' }}>
                {priceText(Number(confirmMove.price))}/month
              </strong>{' '}
              from their next payment. Nothing is charged now, and each one gets an in-app
              notification.
            </>
          ) : null
        }
        onConfirm={async () => {
          if (confirmMove) await runMove(confirmMove);
          setConfirmMove(null);
        }}
        onCancel={() => setConfirmMove(null)}
      />
    </div>
  );
};

export default Subscriptions;
